<template>
  <div class="report-details-page">
    <!-- Page Header -->
    <div class="page-header">
      <div class="breadcrumb-nav">
        <router-link :to="{ name: 'chatbot' }" class="breadcrumb-link">
          <i class="las la-home"></i>
          Financial Assistant
        </router-link>
        <i class="las la-chevron-right breadcrumb-separator"></i>
        <router-link :to="`/manage/chatbot/candidate/${candidateId}/sessions`" class="breadcrumb-link">
          Chat Sessions
        </router-link>
        <i class="las la-chevron-right breadcrumb-separator"></i>
        <router-link :to="`/manage/chatbot/candidate/${candidateId}/files`" class="breadcrumb-link">
          Files
        </router-link>
        <i class="las la-chevron-right breadcrumb-separator"></i>
        <span class="breadcrumb-current">Report Details</span>
      </div>
      <h1 class="page-title ml-3">Report Details</h1>
    </div>

    <!-- Main Content -->
    <div class="main-content">
      <div v-if="loading" class="loading-state">
        <div class="loading-spinner"></div>
        <p>Loading report details...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <div class="error-icon">
          <i class="las la-exclamation-triangle"></i>
        </div>
        <h3>Error Loading Report</h3>
        <p>{{ error }}</p>
        <div class="error-actions">
          <button @click="loadReportAnalysis" class="retry-btn">
            <i class="las la-redo"></i>
            Try Again
          </button>
          <router-link :to="`/manage/chatbot/candidate/${candidateId}/files`" class="back-btn">
            <i class="las la-arrow-left"></i>
            Back to Files
          </router-link>
        </div>
      </div>

      <div v-else-if="reportAnalysis" class="report-details-content">
        <!-- Report Overview Card -->
        <div class="report-overview-card">
          <div class="report-header">
            <i class="las la-chart-bar"></i>
            <div class="report-info">
              <h2 class="report-title">Credit Model Report</h2>
              <div class="report-meta">
                <span class="file-name" v-if="fileName">{{ fileName }}</span>
                <span class="report-date">{{ formatDate(new Date()) }}</span>
              </div>
            </div>
            <div class="report-actions-header">
              <button @click="generateReport" class="action-btn primary" :disabled="generatingReport">
                <BaseSpinner v-if="generatingReport" size="small" />
                <i v-else class="las la-file-pdf"></i>
                {{ reportButtonText }}
              </button>
            </div>
          </div>
        </div>

        <!-- Credit Model Report Content -->
        <div v-if="creditModelData" class="credit-model-content">
          <!-- Risk Rating Summary and Credit Recommendation Side by Side -->
          <div v-if="riskRatingSummary || (analysisData && analysisData.credit_recommendation !== undefined)" class="summary-recommendation-grid">
            <!-- Risk Rating Summary Card -->
            <div class="risk-rating-wrapper">
              <RiskRatingSummary v-if="riskRatingSummary" :summary="riskRatingSummary" />
            </div>

            <!-- Credit Recommendation -->
            <div v-if="analysisData && analysisData.credit_recommendation !== undefined" class="recommendation-wrapper">
              <div class="recommendation-card-compact">
                <div class="recommendation-content">
                  <span class="recommendation-label">Credit Recommendation:</span>
                  <div v-if="!editingRecommendation" class="recommendation-display">
                    <span class="recommendation-badge-compact" :class="getRecommendationClass(analysisData.credit_recommendation)">
                      {{ analysisData.credit_recommendation }}
                    </span>
                    <i 
                      @click="editingRecommendation = true" 
                      class="las la-edit edit-icon-small" 
                      title="Edit"
                    ></i>
                  </div>
                  <div v-else class="recommendation-edit">
                    <select v-model="editingRecommendationValue" class="recommendation-select">
                      <option value="ACCEPT">ACCEPT</option>
                      <option value="REJECT">REJECT</option>
                      <option value="DECLINE">DECLINE</option>
                      <option value="REVIEW">REVIEW</option>
                    </select>
                    <i 
                      @click="saveRecommendation" 
                      class="las la-check save-icon-small" 
                      title="Save"
                    ></i>
                    <i 
                      @click="cancelEditRecommendation" 
                      class="las la-times cancel-icon-small" 
                      title="Cancel"
                    ></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Company Description Section -->
          <div v-if="riskRatingSummary" class="company-description-section">
            <label class="description-label">Company Description</label>
            <textarea
              v-model="companyDescription"
              class="company-description-input"
              placeholder="Enter company description..."
              rows="4"
              @input="onDescriptionChange"
            ></textarea>
          </div>

          <!-- Analysis Data Section -->
          <div v-if="analysisData" class="analysis-data-section">

            <!-- Key Strengths & Weaknesses Grid -->
            <div class="analysis-grid-compact">
              <AnalysisCard 
                title="Key Strengths" 
                :items="analysisData.key_strengths" 
                card-class="strengths-card"
                @update="updateAnalysisData('key_strengths', $event)"
              />
              <AnalysisCard 
                title="Key Weaknesses" 
                :items="analysisData.key_weaknesses" 
                card-class="weaknesses-card"
                @update="updateAnalysisData('key_weaknesses', $event)"
              />
            </div>

            <!-- Relative Risk Strengths & Weaknesses -->
            <div class="analysis-grid-compact">
              <AnalysisCard 
                title="Risk Strengths" 
                :items="analysisData.relative_risk_strengths" 
                card-class="risk-strengths-card"
                @update="updateAnalysisData('relative_risk_strengths', $event)"
              />
              <AnalysisCard 
                title="Risk Weaknesses" 
                :items="analysisData.relative_risk_weaknesses" 
                card-class="risk-weaknesses-card"
                @update="updateAnalysisData('relative_risk_weaknesses', $event)"
              />
            </div>

            <!-- Other Analysis Cards -->
            <AnalysisCard 
              title="Revenue Trends" 
              :items="analysisData.revenue_trends" 
              card-class="trends-card"
              @update="updateAnalysisData('revenue_trends', $event)"
            />
            <AnalysisCard 
              title="Cash & Quick Ratio Analysis" 
              :items="analysisData.cash_quick_ratio" 
              card-class="ratio-card"
              @update="updateAnalysisData('cash_quick_ratio', $event)"
            />
            <AnalysisCard 
              title="Recommendation Points" 
              :items="analysisData.recommendation_points" 
              card-class="recommendation-points-card"
              @update="updateAnalysisData('recommendation_points', $event)"
            />
            <AnalysisCard 
              title="Conclusion" 
              :items="analysisData.conclusion_points" 
              card-class="conclusion-card"
              @update="updateAnalysisData('conclusion_points', $event)"
            />
          </div>

          <!-- Collapsible Sections for Each Sheet -->
          <div class="sheets-container">
            <!-- Risk Rating Model -->
            <div v-if="creditModelData['Risk Rating Model']" class="sheet-section">
              <div class="sheet-header" @click="toggleSection('riskRating')">
                <h3 class="sheet-title">
                  Risk Rating Model
                </h3>
                <i class="las" :class="expandedSections.riskRating ? 'la-angle-up' : 'la-angle-down'"></i>
              </div>
              <div v-show="expandedSections.riskRating" class="sheet-content">
                <div class="risk-tables-grid">
                  <RiskRatingTable
                    v-for="(table, tableName) in creditModelData['Risk Rating Model'].structured_tables"
                    :key="tableName"
                    :title="tableName"
                    :table-data="table"
                  />
                </div>
              </div>
            </div>

            <!-- Monthly Cash Flow -->
            <div v-if="creditModelData['Monthly Cash Flow']" class="sheet-section">
              <div class="sheet-header" @click="toggleSection('monthlyCashFlow')">
                <h3 class="sheet-title">
                  Monthly Cash Flow
                </h3>
                <i class="las" :class="expandedSections.monthlyCashFlow ? 'la-angle-up' : 'la-angle-down'"></i>
              </div>
              <div v-show="expandedSections.monthlyCashFlow" class="sheet-content">
                <MonthlyCashFlowTable
                  :monthly-data="creditModelData['Monthly Cash Flow'].structured_tables"
                />
              </div>
            </div>

          </div>
        </div>


      </div>
    </div>
  </div>
</template>

<script>
import ChatbotService from '@/services/chatbot.service'
import BaseSpinner from '@/components/UI/BaseSpinner.vue'
import RiskRatingSummary from './components/creditReport/RiskRatingSummary.vue'
import AnalysisCard from './components/creditReport/AnalysisCard.vue'
import RiskRatingTable from './components/creditReport/RiskRatingTable.vue'
import MonthlyCashFlowTable from './components/creditReport/MonthlyCashFlowTable.vue'

export default {
  name: 'ReportDetails',
  components: {
    BaseSpinner,
    RiskRatingSummary,
    AnalysisCard,
    RiskRatingTable,
    MonthlyCashFlowTable
  },
  props: {
    candidateId: {
      type: String,
      required: true
    },
    fileId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      reportAnalysis: null,
      fileName: null,
      loading: false,
      error: null,
      generatingReport: false,
      expandedSections: {
        riskRating: true,
        monthlyCashFlow: false
      },
      editingRecommendation: false,
      editingRecommendationValue: '',
      candidate: null,
      companyDescription: ''
    }
  },
  watch: {
    'analysisData.credit_recommendation': {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.editingRecommendationValue = newVal
        }
      }
    },
    
    'reportAnalysis.company_overview': {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.companyDescription = newVal
        }
      }
    }
  },
  mounted() {
    this.loadReportAnalysis()
    this.loadFileName()
    this.loadCandidate()
  },
  computed: {
    reportButtonText() {
      if (this.generatingReport) {
        return 'Generating...'
      }
      return 'Generate Credit Model Report'
    },
    
    creditModelData() {
      if (!this.reportAnalysis) return null
      return this.reportAnalysis['credit_model_data'] || null
    },
    
    analysisData() {
      if (!this.reportAnalysis) return null
      return this.reportAnalysis.analysis_data || null
    },
    
    riskRatingSummary() {
      console.log('creditModelData', this.creditModelData['Risk Rating Model'])
      if (!this.creditModelData || !this.creditModelData['Risk Rating Model']) return null
      
      const overallRisk = this.creditModelData['Risk Rating Model'].structured_tables?.Overall_Risk_Summary
      if (!overallRisk || !Array.isArray(overallRisk)) return null
      
      const risks = []
      let totalScore = null
      let rating = null
      
      overallRisk.forEach(item => {
        if (item.Risk === 'Total') {
          totalScore = item.Obtained_Score
        } else if (item.Risk === 'Risk Rating') {
          rating = item.Obtained_Score
        } else {
          risks.push({
            name: item.Risk,
            obtained: item.Obtained_Score,
            total: item.Total_Score,
            percentage: typeof item.Percentage === 'number' ? item.Percentage : parseFloat(item.Percentage) || 0
          })
        }
      })
      
      return {
        totalScore,
        rating: rating || '',
        risks
      }
    },
    
  },
  methods: {
    async loadReportAnalysis() {
      this.loading = true
      this.error = null
      
      try {
        const analysis = await ChatbotService.getReportAnalysis(this.fileId, this.candidateId)
        this.reportAnalysis = analysis.data
        console.log('reportAnalysis', this.reportAnalysis)
      } catch (err) {
        console.error('Error loading report analysis:', err)
        this.error = 'Failed to load report analysis. Please try again.'
      } finally {
        this.loading = false
      }
    },

    async loadFileName() {
      try {
        const fileDetails = await ChatbotService.getFileDetails(this.fileId)
        this.fileName = fileDetails.name || fileDetails.file_name || 'Unknown File'
      } catch (err) {
        console.error('Error loading file name:', err)
        // Don't set error here, just use default
      }
    },

    async generateReport() {
      console.log('generating report', this.candidate)
      this.generatingReport = true
      try {
        // Generate credit model report (PDF) with analysis data
        const payload = {
          file_id: this.fileId
        }
        
        // Include analysis_data if it exists and has been modified
        if (this.analysisData) {
          payload.analysis_data = this.analysisData
        }
        
        // Include company description if provided
        if (this.companyDescription && this.companyDescription.trim()) {
          payload.company_description = this.companyDescription.trim()
        }
        
        console.log('payload', payload)
        const blob = await ChatbotService.generateCreditModelReport(this.fileId, this.candidateId, payload)
        
        // Ensure we have a proper Blob object
        if (!(blob instanceof Blob)) {
          throw new Error('Invalid report data received from server')
        }
        
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        const fileName = `${this.candidate?.name || 'credit_model_report'}_Credit_Report.pdf`
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        this.$bvToast.toast('Credit model report generated and downloaded successfully!', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        })
      } catch (err) {
        console.error('Error generating report:', err)
        this.$bvToast.toast('Error generating credit model report: ' + (err.message || 'Unknown error'), {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        })
      } finally {
        this.generatingReport = false
      }
    },


    formatDate(date) {
      if (!date) return 'Unknown'
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    
    formatPercentage(value) {
      if (value === null || value === undefined || value === '') return '0%'
      const num = typeof value === 'number' ? value : parseFloat(value)
      if (isNaN(num)) return String(value)
      return (num * 100).toFixed(0) + '%'
    },
    
    formatCurrency(value) {
      if (value === null || value === undefined || value === '') return '-'
      const num = typeof value === 'number' ? value : parseFloat(value)
      if (isNaN(num)) return String(value)
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(num)
    },
    
    formatCellValue(value) {
      if (value === null || value === undefined || value === '') return '-'
      if (typeof value === 'number') {
        // Format numbers with appropriate decimals
        if (Number.isInteger(value)) return value.toLocaleString()
        return value.toFixed(2)
      }
      return String(value)
    },
    
    getTableHeaders(table) {
      if (!Array.isArray(table) || table.length === 0) return []
      const firstRow = table[0]
      return Object.keys(firstRow)
    },
    
    getCellClass(key, value) {
      const classes = []
      if (key.includes('Score') || key.includes('Percentage')) {
        classes.push('score-cell')
      }
      if (key.includes('Risk') && typeof value === 'string' && value.includes('Risk')) {
        classes.push('risk-header-cell')
      }
      return classes.join(' ')
    },
    
    getRiskColorClass(percentage) {
      if (percentage >= 0.8) return 'risk-low'
      if (percentage >= 0.6) return 'risk-medium'
      return 'risk-high'
    },
    
    toggleSection(section) {
      this.expandedSections[section] = !this.expandedSections[section]
    },
    
    getRecommendationClass(recommendation) {
      const rec = recommendation?.toUpperCase() || ''
      if (rec === 'ACCEPT') return 'recommendation-accept'
      if (rec === 'REJECT' || rec === 'DECLINE') return 'recommendation-reject'
      return 'recommendation-review'
    },
    
    updateAnalysisData(field, value) {
      if (this.analysisData) {
        this.$set(this.analysisData, field, value)
        // Also update the reportAnalysis to keep it in sync
        if (this.reportAnalysis) {
          this.$set(this.reportAnalysis, 'analysis_data', { ...this.analysisData })
        }
      }
    },
    
    saveRecommendation() {
      if (this.analysisData && this.editingRecommendationValue) {
        this.$set(this.analysisData, 'credit_recommendation', this.editingRecommendationValue)
        this.updateAnalysisData('credit_recommendation', this.editingRecommendationValue)
        this.editingRecommendation = false
      }
    },
    
    cancelEditRecommendation() {
      this.editingRecommendation = false
      this.editingRecommendationValue = this.analysisData?.credit_recommendation || ''
    },

    async loadCandidate() {
      this.candidate = await ChatbotService.getFinancingCandidate(this.candidateId)
      console.log('candidate', this.candidate)
    },
    
    onDescriptionChange() {
      // Description is automatically saved via v-model
      // This method can be used for additional logic if needed
    }
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';

.report-details-page {
  background: #ffffff;
  min-height: 100vh;
}

.page-header {
  background: #ffffff;
  padding: 2rem;
  border-bottom: 1px solid #e9ecef;
  
  .breadcrumb-nav {
    display: flex;
    align-items: center;
    margin-bottom: 1rem;
    font-size: 0.9rem;
    
    .breadcrumb-link {
      color: #6c757d;
      text-decoration: none;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      transition: color 0.2s ease;
      
      &:hover {
        color: $color-primary;
      }
    }
    
    .breadcrumb-separator {
      color: #adb5bd;
      margin: 0 0.5rem;
    }
    
    .breadcrumb-current {
      color: #495057;
      font-weight: 500;
    }
  }
  
  .page-title {
    font-size: 2rem;
    font-weight: 700;
    color: #2d3748;
    margin: 0 0 0.5rem 0;
    font-family: $brand-font-medium;
  }
}

.main-content {
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.loading-state, .error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  
  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f4f6;
    border-top: 4px solid $color-primary;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }
  
  p {
    color: #6c757d;
    font-size: 1.1rem;
    margin: 0;
  }
}

.error-state {
  .error-icon {
    font-size: 3rem;
    color: #e53e3e;
    margin-bottom: 1rem;
  }
  
  h3 {
    color: #2d3748;
    margin: 0 0 1rem 0;
    font-size: 1.5rem;
  }
  
  .error-actions {
    display: flex;
    gap: 1rem;
    margin-top: 2rem;
  }
  
  .retry-btn, .back-btn {
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 8px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: $brand-font-medium;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .retry-btn {
    background: $color-primary;
    color: white;
    
    &:hover {
      background: darken($color-primary, 10%);
    }
  }
  
  .back-btn {
    background: #f8f9fa;
    color: #6c757d;
    border: 1px solid #e2e8f0;
    
    &:hover {
      background: #e9ecef;
    }
  }
}

.report-details-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.report-overview-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  .report-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;

    i {
      font-size: 3rem;
      color: $color-primary;
    }
    
    .report-info {
      flex: 1;
      
      .report-title {
        font-size: 1.5rem;
        font-weight: 600;
        color: #2d3748;
        margin: 0 0 0.5rem 0;
        font-family: $brand-font-medium;
      }
      
      .report-meta {
        display: flex;
        gap: 1rem;
        flex-wrap: wrap;
        
        span {
          color: #6c757d;
          font-size: 0.9rem;
          font-family: $brand-font-regular;
        }
      }
    }
    
    .report-actions-header {
      display: flex;
      align-items: center;
      gap: 1rem;
    }
  }
}


.report-actions-header {
  .action-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 8px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: $brand-font-medium;
    
    &.primary {
      background: $color-primary;
      color: $color-secondary;
      
      &:hover:not(:disabled) {
        background: darken($color-primary, 10%);
      }

      i {
        color: #ef4444;
        font-size: 1.6rem;
      }
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
    }
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Credit Model Report Styles */
.credit-model-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.summary-recommendation-grid {
  display: grid;
  grid-template-columns: 80% 19%;
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.risk-rating-wrapper {
  width: 100%;
}

.recommendation-wrapper {
  width: 100%;
}

.company-description-section {
  margin-top: 1rem;
}

.description-label {
  display: block;
  font-size: 0.95rem;
  font-weight: 600;
  color: #495057;
  font-family: $brand-font-medium;
  letter-spacing: 0.3px;
  margin-bottom: 0.5rem;
}

.company-description-input {
  width: 100%;
  padding: 0.875rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: $brand-font-regular;
  color: #2d3748;
  line-height: 1.6;
  resize: vertical;
  min-height: 100px;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: $color-primary;
    box-shadow: 0 0 0 3px rgba($color-primary, 0.1);
  }
  
  &::placeholder {
    color: #adb5bd;
  }
}

/* Analysis Data Styles */
.analysis-data-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.recommendation-card-compact {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.recommendation-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: flex-start;
}

.recommendation-label {
  font-size: 0.95rem;
  font-weight: 600;
  color: #495057;
  font-family: $brand-font-medium;
  letter-spacing: 0.3px;
}

.recommendation-badge-compact {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.625rem 1.5rem;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 700;
  font-family: $brand-font-medium;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &.recommendation-accept {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: white;
  }
  
  &.recommendation-reject {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
  }
  
  &.recommendation-review {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
    color: white;
  }
}


.sheets-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.sheet-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  cursor: pointer;
  
  &:hover {
    opacity: 0.8;
  }
}

.sheet-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
  font-family: $brand-font-medium;
}

.sheet-content {
  padding: 1rem 0;
}

.risk-tables-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}




.recommendation-display {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  flex-wrap: wrap;
}

.edit-icon-small {
  cursor: pointer;
  color: #6c757d;
  font-size: 1.2rem;
  padding: 0.5rem;
  border-radius: 6px;
  background: #f8f9fa;
  
  &:hover {
    background: #e9ecef;
    color: $color-primary;
  }
}

.recommendation-edit {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  flex-wrap: wrap;
}

.recommendation-select {
  padding: 0.625rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 700;
  font-family: $brand-font-medium;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  background: white;
  color: #2d3748;
  min-width: 150px;
  
  &:focus {
    outline: none;
    border-color: $color-primary;
    box-shadow: 0 0 0 3px rgba($color-primary, 0.15);
  }
  
  &:hover {
    border-color: #cbd5e0;
  }
  
  option {
    text-transform: uppercase;
    padding: 0.5rem;
  }
}

.save-icon-small {
  cursor: pointer;
  color: #10b981;
  font-size: 1.2rem;
  padding: 0.5rem;
  border-radius: 6px;
  background: #d1fae5;
  
  &:hover {
    background: #10b981;
    color: white;
  }
}

.cancel-icon-small {
  cursor: pointer;
  color: #dc2626;
  font-size: 1.2rem;
  padding: 0.5rem;
  border-radius: 6px;
  background: #fee2e2;
  
  &:hover {
    background: #dc2626;
    color: white;
  }
}

@media (max-width: 768px) {
  .main-content {
    padding: 1rem;
  }
  
  .report-header {
    flex-direction: column;
    text-align: center;
    
    .report-actions-header {
      width: 100%;
      justify-content: center;
      margin-top: 1rem;
    }
  }
  
  .analysis-grid-compact {
    grid-template-columns: 1fr;
  }
  
  .risk-tables-grid {
    grid-template-columns: 1fr;
  }
}
</style>

